import React, { useState, useCallback, useMemo } from "react";
import type { InventoryItem, SlotLocation } from "./types";
import BackgroundImage from "../../components/ui/BackgroundImage";
import bgImg from "./assets/images/background.webp";
import { useInventory } from "./hooks/useInventory";
import { useContextMenu } from "./hooks/useContextMenu";
import { DragDropProvider } from "./context/DragDropContext";
import { Header } from "./components/Header";
import { NearbyItems } from "./components/NearbyItems";
import { CarTrunk } from "./components/CarTrunk";
import { CharacterItems } from "./components/CharacterItems";
import { FastItems } from "./components/FastItems";
import { InventorySection } from "./components/InventorySection";
import { BackpackSection } from "./components/BackpackSection";
import { ContextMenu } from "./components/ContextMenu";
import { DragGhost } from "./components/DragGhost";
import { MyDocumentsModal } from "./components/MyDocumentsModal";

interface Props {
  className?: string;
  onClose?: () => void;
}

const InventoryRender: React.FC<Props> = ({ className, onClose }) => {
  const {
    nearbyItems,
    carTrunk,
    inventory,
    backpack,
    fastItems,
    equipment,
    capacities,
    carPlate,
    player,
    selectedLocation,
    selectItem,
    consumeItem,
    dropItem,
    takeItem,
    splitItem,
    moveItem,
    canDrop,
  } = useInventory();

  const {
    contextMenu,
    open: openContextMenuRaw,
    close: closeContextMenu,
  } = useContextMenu();

  const openContextMenu = useCallback(
    (e: React.MouseEvent, item: InventoryItem, location?: SlotLocation) =>
      openContextMenuRaw(e, item, location),
    [openContextMenuRaw],
  );

  const [showDocuments, setShowDocuments] = useState(false);

  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handleDeselectAll = useCallback(() => {
    selectItem(null);
    closeContextMenu();
  }, [selectItem, closeContextMenu]);
  const handleItemClick = useCallback(
    (_item: InventoryItem, location?: SlotLocation) => {
      selectItem(location ?? null);
      closeContextMenu();
    },
    [selectItem, closeContextMenu],
  );
  const handleShowDocuments = useCallback(() => setShowDocuments(true), []);
  const handleHideDocuments = useCallback(() => setShowDocuments(false), []);
  const handleUse   = useCallback(() => { if (contextMenu.item) consumeItem(contextMenu.item, contextMenu.item.quantity); }, [contextMenu.item, consumeItem]);
  const handleDrop  = useCallback(() => { if (contextMenu.item) dropItem(contextMenu.item, contextMenu.item.quantity); }, [contextMenu.item, dropItem]);
  const handleTake  = useCallback(() => { if (contextMenu.item) takeItem(contextMenu.item, contextMenu.item.quantity); }, [contextMenu.item, takeItem]);
  const handleSplit = useCallback((qty: number) => {
    if (contextMenu.item && contextMenu.location) splitItem(contextMenu.item, contextMenu.location, qty);
  }, [contextMenu.item, contextMenu.location, splitItem]);

  const sharedProps = useMemo(
    () => ({
      selectedLocation,
      onItemClick: handleItemClick,
      onItemRightClick: openContextMenu,
    }),
    [selectedLocation, handleItemClick, openContextMenu],
  );

  return (
    <DragDropProvider onDrop={moveItem} canDrop={canDrop}>
      <BackgroundImage imageSrc={bgImg}>
        <div
          className={`w-full h-screen flex flex-col gap-2 1440px:gap-4 2560px:gap-6 purista-font overflow-hidden p-5 1440px:p-8 1600px:p-10 2560px:p-16 ${className ?? ""}`}
          onClick={handleDeselectAll}
        >
          <Header
            playerName={player.name}
            money={player.money}
            weight={player.weight}
            onClose={handleClose}
            onMyDocuments={handleShowDocuments}
          />

          <div className="flex gap-5 1366px:gap-6 1440px:gap-8 1600px:gap-12 2560px:gap-[120px] flex-1 min-h-0 mt-1 1366px:mt-2 1440px:mt-3 1600px:mt-5 2560px:mt-8">
            <div className="flex flex-col gap-5 1366px:gap-6 1440px:gap-8 1600px:gap-[45px] 2560px:gap-[90px] flex-1 min-w-0">
              <NearbyItems
                items={nearbyItems}
                capacity={capacities.nearby}
                {...sharedProps}
              />
              <CarTrunk
                items={carTrunk}
                carPlate={carPlate}
                capacity={capacities.carTrunk}
                {...sharedProps}
              />
            </div>

            <div className="flex flex-col gap-2 1366px:gap-3 1440px:gap-5 1600px:gap-[40px] 2560px:gap-[90px] flex-1 min-w-0">
              <CharacterItems equipment={equipment} {...sharedProps} />
              <FastItems items={fastItems} {...sharedProps} className="mb-20" />
            </div>

            <div className="flex flex-col gap-5 1366px:gap-6 1440px:gap-8 1600px:gap-[45px] 2560px:gap-[90px] flex-1 min-w-0">
              <InventorySection
                items={inventory}
                capacity={capacities.inventory}
                {...sharedProps}
              />
              <BackpackSection
                items={backpack}
                capacity={capacities.backpack}
                {...sharedProps}
              />
            </div>
          </div>
        </div>

        <ContextMenu
          state={contextMenu}
          onClose={closeContextMenu}
          onUse={handleUse}
          onDrop={handleDrop}
          onTake={handleTake}
          onSplit={handleSplit}
        />

        <DragGhost />
        {showDocuments && <MyDocumentsModal onClose={handleHideDocuments} />}
      </BackgroundImage>
    </DragDropProvider>
  );
};

export default InventoryRender;
